import { STANDARD_BRANCH } from '@festina-life/api'
import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { useLayoutContext } from '../../../../context/layoutContext'
import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useIsPersonaEditor } from '../../../../uiModels/useIsPersonaEditor'
import { useRequiredBranchOperationsContext } from '../../../../workspace/operationsContext'
import { ManageSection } from '../../../manageSection/ManageSection'
import { CatchupBranchButton } from './CatchupBranchButton'
import { ChangeBranchBaseButton } from './ChangeBranchBaseButton'
import { ChangeFollowBranchButton } from './ChangeFollowBranchButton'
import { CloseBranchButton } from './CloseBranchButton'
import { CreateChildBranchButton } from './CreateChildBranchButton'
import { DeleteBranchButton } from './DeleteBranchButton'
import { DownloadConfigurationButton } from './DownloadConfigurationButton'
import { ForceDeleteBranchButton } from './ForceDeleteBranchButton'
import { ForcePushToBaseBranchButton } from './ForcePushToBaseBranchButton'
import { MergeBranchButton } from './MergeBranchButton'
import { NavigateToBranchButton } from './NavigateToBranchButton'
import { PushToBaseBranchPersonaAwareButton } from './PushToBaseBranchPersonaAwareButton'
import { RebaseBranchButton } from './RebaseBranchButton'
import { SquashBranchButton } from './SquashBranchButton'
import { UpgradeBranchButton } from './UpgradeBranchButton'

export const ManageBranch = () => {
  const translate = useTranslate()
  const { branchKey } = useRequiredBranchOperationsContext()
  const isStandardBranch = branchKey.name === STANDARD_BRANCH
  const conciseBranch = useConciseBranch({
    branchName: branchKey.name,
    server: branchKey.server,
    shouldFallbackToStandard: true,
  })
  const conciseBaseBranch = useConciseBranch({
    branchName: conciseBranch?.baseBranchName ?? '',
    server: branchKey.server,
    shouldFallbackToStandard: false,
  })
  const isPersonaEditor = useIsPersonaEditor()
  const { persona } = useLayoutContext()

  assertNotEmpty(conciseBranch)

  const isProtectedBranch = isProtectedBranchCategory(
    conciseBranch.branchCategory,
  )
  const isNonStandardNonProtectedBranch =
    !isStandardBranch && !isProtectedBranch
  const hasBaseBranch = !!conciseBranch.baseBranchName
  const isBaseBranchProtected =
    conciseBaseBranch &&
    isProtectedBranchCategory(conciseBaseBranch.branchCategory)

  return (
    <div className="flex flex-col gap-y-4 p-1">
      <ManageSection
        title={translate('branchDetailsPanel.tabs.manage.section.common.title')}
        options={[
          {
            label: translate('branchDetailsPanel.tabs.manage.openBranch'),
            description: translate(
              'branchDetailsPanel.tabs.manage.section.common.help.open',
            ),
            button: <NavigateToBranchButton branchKey={branchKey} />,
          },
          {
            label: translate('branchDetailsPanel.tabs.manage.closeBranch'),
            description: translate(
              'branchDetailsPanel.tabs.manage.section.common.help.close',
            ),
            button: <CloseBranchButton branchKey={branchKey} />,
          },
          ...(!isStandardBranch && isPersonaEditor
            ? [
                {
                  label: translate(
                    'branchDetailsPanel.tabs.manage.createBranch',
                  ),
                  description: translate(
                    'branchDetailsPanel.tabs.manage.section.common.help.open',
                  ),
                  button: <CreateChildBranchButton branchKey={branchKey} />,
                },
              ]
            : []),
          ...(isNonStandardNonProtectedBranch && isPersonaEditor
            ? [
                {
                  label: translate(
                    'branchDetailsPanel.tabs.manage.deleteBranch',
                  ),
                  description: translate(
                    'branchDetailsPanel.tabs.manage.section.common.help.delete',
                  ),
                  button: <DeleteBranchButton branchKey={branchKey} />,
                },
              ]
            : []),
          ...(isProtectedBranch && persona === 'ConfigurationAdmin'
            ? [
                {
                  label: translate(
                    'branchDetailsPanel.tabs.manage.forceDeleteBranch',
                  ),
                  description: translate(
                    'branchDetailsPanel.tabs.manage.section.common.help.forceDelete',
                  ),
                  button: <ForceDeleteBranchButton branchKey={branchKey} />,
                },
              ]
            : []),
        ]}
      />
      {(isNonStandardNonProtectedBranch || hasBaseBranch) &&
        isPersonaEditor && (
          <ManageSection
            title={translate(
              'branchDetailsPanel.tabs.manage.section.update.title',
            )}
            options={[
              ...(hasBaseBranch
                ? [
                    {
                      label: translate(
                        'branchDetailsPanel.tabs.manage.pushToBaseBranch',
                      ),
                      description: translate(
                        'branchDetailsPanel.tabs.manage.section.update.help.push',
                      ),
                      button: (
                        <PushToBaseBranchPersonaAwareButton
                          branchKey={branchKey}
                        />
                      ),
                    },
                    ...(persona === 'ConfigurationAdmin' &&
                    isBaseBranchProtected
                      ? [
                          {
                            label: translate(
                              'branchDetailsPanel.tabs.manage.forcePushToBaseBranch',
                            ),
                            description: translate(
                              'branchDetailsPanel.tabs.manage.section.update.help.forcePush',
                            ),
                            button: (
                              <ForcePushToBaseBranchButton
                                branchKey={branchKey}
                              />
                            ),
                          },
                        ]
                      : []),
                  ]
                : []),
              ...(isNonStandardNonProtectedBranch
                ? [
                    {
                      label: translate('branchDetailsPanel.tabs.manage.rebase'),
                      description: translate(
                        'branchDetailsPanel.tabs.manage.section.update.help.rebase',
                      ),
                      button: <RebaseBranchButton branchKey={branchKey} />,
                    },
                    {
                      label: translate(
                        'branchDetailsPanel.tabs.manage.changeBaseBranch',
                      ),
                      description: translate(
                        'branchDetailsPanel.tabs.manage.section.update.help.changeBase',
                      ),
                      button: <ChangeBranchBaseButton branchKey={branchKey} />,
                    },
                    {
                      label: translate('branchDetailsPanel.tabs.manage.squash'),
                      description: translate(
                        'branchDetailsPanel.tabs.manage.section.update.help.squash',
                      ),
                      button: <SquashBranchButton branchKey={branchKey} />,
                    },
                    {
                      label: translate(
                        'branchDetailsPanel.tabs.manage.manageFollows',
                      ),
                      description: translate(
                        'branchDetailsPanel.tabs.manage.section.update.help.catchup',
                      ),
                      button: <CatchupBranchButton branchKey={branchKey} />,
                    },
                    {
                      label: translate(
                        'branchDetailsPanel.tabs.manage.changeFollowBranch',
                      ),
                      description: translate(
                        'branchDetailsPanel.tabs.manage.section.update.help.changeFollow',
                      ),
                      button: (
                        <ChangeFollowBranchButton branchKey={branchKey} />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
      {isPersonaEditor && (
        <ManageSection
          title={translate(
            'branchDetailsPanel.tabs.manage.section.release.title',
          )}
          options={[
            {
              label: translate('branchDetailsPanel.tabs.manage.upgrade'),
              description: translate(
                'branchDetailsPanel.tabs.manage.section.release.help.upgrade',
              ),
              button: <UpgradeBranchButton branchKey={branchKey} />,
            },
            {
              label: translate('branchDetailsPanel.tabs.manage.merge'),
              description: translate(
                'branchDetailsPanel.tabs.manage.section.release.help.merge',
              ),
              button: <MergeBranchButton branchKey={branchKey} />,
            },
          ]}
        />
      )}
      <ManageSection
        title={translate('branchDetailsPanel.tabs.manage.section.export.title')}
        options={[
          {
            label: translate(
              'branchDetailsPanel.tabs.manage.downloadBranchPackage',
            ),
            description: translate(
              'branchDetailsPanel.tabs.manage.section.export.help.downloadPackage',
            ),
            button: <DownloadConfigurationButton branchKey={branchKey} />,
          },
        ]}
      />
    </div>
  )
}
